import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Building, Phone, Mail, Users, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  company: 'Entreprise',
  association: 'Association',
  administration: 'Administration',
  other: 'Autre',
};

export default function OrganizationsPage() {
  const { token } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: '', type: 'company', address: '', phone: '', email: '', website: '', siret: '', notes: '', tags: []
  });

  useEffect(() => { fetchOrgs(); }, [searchQuery]);

  const fetchOrgs = async () => {
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const { data } = await axios.get(`${API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setOrgs(data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      const payload = { ...form, tags: form.tags.length ? form.tags : [] };
      await axios.post(`${API}/organizations`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Organisation créée');
      setIsCreateOpen(false);
      resetForm();
      fetchOrgs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdate = async () => {
    try {
      await axios.patch(`${API}/organizations/${selected.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Organisation mise à jour');
      setIsEditOpen(false);
      fetchOrgs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette organisation ? Les contacts et dossiers liés seront désassociés.')) return;
    try {
      await axios.delete(`${API}/organizations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Organisation supprimée');
      fetchOrgs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const resetForm = () => setForm({ name: '', type: 'company', address: '', phone: '', email: '', website: '', siret: '', notes: '', tags: [] });

  const openEdit = (org) => {
    setSelected(org);
    setForm({
      name: org.name, type: org.type || 'company', address: org.address || '',
      phone: org.phone || '', email: org.email || '', website: org.website || '',
      siret: org.siret || '', notes: org.notes || '', tags: org.tags || []
    });
    setIsEditOpen(true);
  };

  const FormFields = () => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nom *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="SARL Compleo" data-testid="org-name" />
        </div>
        <div className="space-y-2">
          <Label>Type</Label>
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="org-type">
            {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Adresse</Label>
        <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="11 avenue Daviot, 06100 Nice" data-testid="org-address" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Téléphone</Label>
          <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+33..." data-testid="org-phone" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="contact@..." data-testid="org-email" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Site web</Label>
          <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://..." data-testid="org-website" />
        </div>
        <div className="space-y-2">
          <Label>SIRET</Label>
          <Input value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} placeholder="123 456 789 00012" data-testid="org-siret" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="org-notes" />
      </div>
    </div>
  );

  return (
    <DashboardLayout title="Organisations">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-muted-foreground">Gérez vos sociétés et structures partenaires</p>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 w-64" data-testid="search-organizations" />
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="create-org-btn"><Plus className="w-4 h-4" /> Nouvelle</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Nouvelle organisation</DialogTitle>
                  <DialogDescription>Ajouter une société ou structure</DialogDescription>
                </DialogHeader>
                <FormFields />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Annuler</Button>
                  <Button onClick={handleCreate} disabled={!form.name} data-testid="save-org-btn">Créer</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card data-testid="organizations-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Dossiers</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Chargement...</TableCell></TableRow>
                ) : orgs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'Aucune organisation trouvée' : 'Aucune organisation. Créez la première.'}
                  </TableCell></TableRow>
                ) : orgs.map(org => (
                  <TableRow key={org.id} className="group" data-testid={`org-row-${org.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-blue-500/10 text-blue-600">
                            {org.name.substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{org.name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {org.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{org.phone}</span>}
                            {org.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{org.email}</span>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{TYPE_LABELS[org.type] || org.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <Users className="w-4 h-4" /> {org.contact_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <FolderKanban className="w-4 h-4" /> {org.dossier_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`org-actions-${org.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(org)}><Pencil className="w-4 h-4 mr-2" />Modifier</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(org.id)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" />Supprimer</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l'organisation</DialogTitle>
              <DialogDescription>Mettre à jour les informations</DialogDescription>
            </DialogHeader>
            <FormFields />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdate} disabled={!form.name} data-testid="update-org-btn">Mettre à jour</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
